import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { getSharedPictures, SERVER_API } from "../../API";
import { Icon } from "../../util/Icon";
import { DropdownPanel } from "../../components/DropdownMultiSelect";

function formatDate(epoch) {
  if (!epoch) return "";
  return new Date(epoch).toLocaleDateString("en-CA");
}

const PRIORITY_LABELS = { 0: "Low", 1: "Medium", 2: "High", 3: "Critical" };
function priorityLabel(level) { return PRIORITY_LABELS[level] ?? String(level); }

const DISPLAY_LABELS = { 0: "", 1: "Primary", 2: "Secondary" };
function displayLabel(value) { return DISPLAY_LABELS[value] ?? String(value); }

function uniqueValues(arr) {
  return Array.from(new Set(arr.filter((v) => v !== null && v !== undefined && v !== ""))).sort();
}

function matches(selected, value) {
  return selected.length === 0 || selected.includes(value);
}

// --- Excel-style column filter ---

function ColumnFilter({ label, options, value, onChange, minWidth }) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const isActive = value.length > 0;

  const handleOpen = () => {
    const rect = btnRef.current.getBoundingClientRect();
    const PANEL_HEIGHT = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const pos = spaceBelow >= PANEL_HEIGHT || spaceBelow >= rect.top
      ? { top: rect.bottom + 2, left: rect.left }
      : { bottom: window.innerHeight - rect.top + 2, left: rect.left };
    setPanelPos(pos);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!panelRef.current?.contains(e.target) && !btnRef.current?.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <th style={{ verticalAlign: "bottom", minWidth: minWidth ?? 110, userSelect: "none" }}>
      <div className="d-flex align-items-center gap-1 fw-semibold">
        <span style={{ whiteSpace: "nowrap" }}>{label}</span>
        <button
          ref={btnRef}
          onClick={handleOpen}
          className="btn btn-sm p-0 border-0 bg-transparent"
          style={{ color: isActive ? "#0d6efd" : "#adb5bd", lineHeight: 1 }}
          title={isActive ? `${value.length} filter(s) active` : "Filter"}
        >
          <Icon iconName={isActive ? "FunnelFill" : "Funnel"} size={13} />
        </button>
      </div>

      {open && createPortal(
        <DropdownPanel
          panelRef={panelRef}
          options={options}
          initialPending={value.length === 0 ? [...options] : [...value]}
          getOptionLabel={(s) => s}
          getOptionValue={(s) => s}
          onConfirm={(selected) => {
            onChange(selected.length === options.length ? [] : selected);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
          style={panelPos}
        />,
        document.body
      )}
    </th>
  );
}

function PlainHeader({ label, minWidth }) {
  return (
    <th style={{ verticalAlign: "bottom", minWidth }}>
      <span className="fw-semibold">{label}</span>
    </th>
  );
}

// --- Main component ---

export default function SharedPicturesReport() {
  const { token } = useParams();

  const [allPhotos, setAllPhotos] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const [filterDate, setFilterDate] = useState([]);
  const [filterTerritory, setFilterTerritory] = useState([]);
  const [filterBanner, setFilterBanner] = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterDisplay, setFilterDisplay] = useState([]);
  const [filterHashtag, setFilterHashtag] = useState([]);

  useEffect(() => {
    getSharedPictures(token)
      .then(({ fromDate, toDate, photos }) => {
        setAllPhotos(photos);
        setFromDate(fromDate);
        setToDate(toDate);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const dateOptions      = useMemo(() => uniqueValues(allPhotos.map((p) => formatDate(p.date))), [allPhotos]);
  const territoryOptions = useMemo(() => uniqueValues(allPhotos.map((p) => p.territoryName)), [allPhotos]);
  const bannerOptions    = useMemo(() => uniqueValues(allPhotos.map((p) => p.bannerName)), [allPhotos]);
  const priorityOptions  = useMemo(() => uniqueValues(allPhotos.map((p) => priorityLabel(p.priorityLevel))), [allPhotos]);
  const displayOptions   = useMemo(() => uniqueValues(allPhotos.map((p) => displayLabel(p.display))), [allPhotos]);
  const hashtagOptions   = useMemo(() => uniqueValues(allPhotos.map((p) => p.hashtag)), [allPhotos]);

  const photos = useMemo(() => allPhotos.filter((p) => {
    if (!matches(filterDate,      formatDate(p.date)))             return false;
    if (!matches(filterTerritory, p.territoryName))                return false;
    if (!matches(filterBanner,    p.bannerName))                   return false;
    if (!matches(filterPriority,  priorityLabel(p.priorityLevel))) return false;
    if (!matches(filterDisplay,   displayLabel(p.display)))        return false;
    if (!matches(filterHashtag,   p.hashtag))                      return false;
    return true;
  }), [allPhotos, filterDate, filterTerritory, filterBanner, filterPriority, filterDisplay, filterHashtag]);

  useEffect(() => {
    if (expandedIndex === null) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") setExpandedIndex((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setExpandedIndex((i) => (i - 1 + photos.length) % photos.length);
      else if (e.key === "Escape") setExpandedIndex(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [expandedIndex, photos.length]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  const manufacturerName = allPhotos.length > 0 ? allPhotos[0].manufacturerName : "";

  const handleExport = () => {
    window.location.href = `${SERVER_API}/reports/pictures/shared/${token}/export`;
  };

  return (
    <div className="container-fluid py-4 px-4">
      <div className="d-flex align-items-start justify-content-between mb-1">
        <h5 className="mb-0">Pictures Report</h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleExport} title="Export to Excel">
          <Icon iconName="FileEarmarkExcel" className="me-1" />
          Export
        </button>
      </div>
      {manufacturerName && <div className="text-muted small mb-1">{manufacturerName}</div>}
      {(fromDate || toDate) && (
        <div className="text-muted small mb-4">
          {fromDate && toDate ? `From ${fromDate} To ${toDate}` : fromDate || toDate}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover mb-0" style={{ fontSize: "0.9rem" }}>
          <thead className="table-light">
            <tr>
              <ColumnFilter label="Date"           options={dateOptions}      value={filterDate}      onChange={setFilterDate} />
              <ColumnFilter label="Territory"      options={territoryOptions} value={filterTerritory} onChange={setFilterTerritory} />
              <ColumnFilter label="Chain"          options={bannerOptions}    value={filterBanner}    onChange={setFilterBanner} />
              <PlainHeader  label="Store" />
              <PlainHeader  label="Note"  minWidth={200} />
              <ColumnFilter label="Priority Level" options={priorityOptions}  value={filterPriority}  onChange={setFilterPriority} />
              <ColumnFilter label="Display"        options={displayOptions}   value={filterDisplay}   onChange={setFilterDisplay} />
              {hashtagOptions.length > 0 && (
                <ColumnFilter label="Hashtag" options={hashtagOptions} value={filterHashtag} onChange={setFilterHashtag} />
              )}
              <PlainHeader  label="Photo" />
            </tr>
          </thead>
          <tbody>
            {photos.length === 0 && (
              <tr>
                <td colSpan={hashtagOptions.length > 0 ? 9 : 8} className="text-center text-muted py-4">
                  No pictures match the current filters.
                </td>
              </tr>
            )}
            {photos.map((p, i) => (
              <tr key={i}>
                <td>{formatDate(p.date)}</td>
                <td>{p.territoryName}</td>
                <td>{p.bannerName}</td>
                <td>{p.storeName}{p.storeNo ? ` ${p.storeNo}` : ""}{p.storeCity ? ` - ${p.storeCity}` : ""}</td>
                <td>{p.note}</td>
                <td>{priorityLabel(p.priorityLevel)}</td>
                <td>{displayLabel(p.display)}</td>
                {hashtagOptions.length > 0 && <td>{p.hashtag}</td>}
                <td style={{ width: 90 }}>
                  <img
                    src={`${SERVER_API}${p.fileUrl}`}
                    alt=""
                    style={{ width: 80, height: 60, objectFit: "cover", cursor: "pointer", borderRadius: 3 }}
                    onClick={() => setExpandedIndex(i)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {expandedIndex !== null && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999 }}
          onClick={() => setExpandedIndex(null)}
        >
          <img
            src={`${SERVER_API}${photos[expandedIndex].fileUrl}`}
            alt=""
            style={{ maxHeight: "90vh", maxWidth: "80vw", objectFit: "contain", borderRadius: 6 }}
          />
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setExpandedIndex((expandedIndex - 1 + photos.length) % photos.length); }}
                style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, color: "#fff", fontSize: 22, cursor: "pointer" }}
              >‹</button>
              <button
                onClick={(e) => { e.stopPropagation(); setExpandedIndex((expandedIndex + 1) % photos.length); }}
                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, color: "#fff", fontSize: 22, cursor: "pointer" }}
              >›</button>
              <div style={{ position: "absolute", bottom: 16, color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                {expandedIndex + 1} / {photos.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
